import { Platform, StyleSheet, Dimensions  } from 'react-native';
import colors from '@/constants/Colors';

export const BASEFONT = 'BMWNext';
export const BASEFONT_BOLD = 'BMWNext-Bold';

export const VIEW_BORDER_RADIUS = 0;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const { width } = Dimensions.get('window');
const vw = width / 100;

const baseFontSize = 16;

const _text = StyleSheet.create({
    h1: {
        fontSize: 20,
        lineHeight: 23,
        fontFamily: BASEFONT_BOLD
    },

    d1: {
        //fontSize: 35,
        //lineHeight: 42,
        fontSize: clamp(3 * vw, 22, 35), //Test on React native
        lineHeight: clamp(3.5 * vw, 26, 42), //Test on React native
        fontFamily: BASEFONT_BOLD
    },

    d2: {
        //fontSize: 30,
        //lineHeight: 36,
        fontSize: clamp(2.4 * vw, 14, 30), //Test on React native
        lineHeight: clamp(2.6 * vw, 17, 36), //Test on React native
        fontFamily: BASEFONT_BOLD
    },

    d3: {
        ...Platform.select({
            native: {
                fontSize: 1.8 * vw,
                lineHeight: 2 * vw,
                //fontSize: 25,
                //lineHeight: 30,
            },
            web: {
                fontSize: '1.8vw',
                lineHeight: '2vw',
            }
        }),
        fontFamily: BASEFONT_BOLD
    },

    d4: {
        fontSize: 20,
        lineHeight: 24,
        fontFamily: BASEFONT_BOLD
    },

    xh1: {
        fontSize: 26,
        lineHeight: 29,
    },

    h2: {
        fontSize: 17,
        lineHeight: 20,
        fontFamily: BASEFONT_BOLD
    },

    h3: {
        fontSize: 14,
        lineHeight: 17,
        fontFamily: BASEFONT_BOLD
    },

    h4: {
        fontSize: 12,
        lineHeight: 15,
        fontFamily: BASEFONT_BOLD
    },

    h5: {
        fontSize: 10,
        lineHeight: 13,
        fontFamily: BASEFONT
    },

    regular: {
        fontFamily: BASEFONT
    },

    bold: {
        fontFamily: BASEFONT_BOLD
    },


    lineThrough: {
        ...Platform.select({
            native: {
                textDecorationLine: "line-through",
                textDecorationStyle: "solid",
                textDecorationColor: "#000"
            },
            web: {
                textDecorationLine: "line-through",
                textDecorationStyle: "solid",
                textDecorationColor: "#000"
            }
        })
    },
    
    text: {},

    subline: {},
    
    xsmall: {
        ...Platform.select({
            native: {
                fontSize: 9,
                lineHeight: 12,
            },
            web: {
                fontSize: 10,
                lineHeight: '1.5em',
            }
        }),
    },
    small: {
        ...Platform.select({
            native: {
                fontSize: 11,
                lineHeight: 13,
            },
            web: {
                fontSize: 12,
                lineHeight: 14.4,
            }
        }),
    },

    italic: {},
    pbold: {
        fontWeight: 'bold',
        fontFamily: 'Arial',
        ...Platform.select({
            native: {
                fontSize: baseFontSize,
                lineHeight: baseFontSize*1.25,
            },
            web: {
                fontSize: baseFontSize + 'px',
                lineHeight: baseFontSize*1.3 + 'px',
            }
        }),
    },
    
    underline: {},
    
    p: {
        fontFamily: 'Arial',
        whiteSpace: 'normal',
        ...Platform.select({
            native: {
                fontSize: baseFontSize,
                lineHeight: baseFontSize*1.25,
            },
            web: {
                fontSize: baseFontSize + 'px',
                lineHeight: baseFontSize*1.3 + 'px',
            }
        }),
    },
    
    uc: {
        textTransform: 'uppercase',
    },
    ucFirst: {
        textTransform: 'capitalize',
    },
    
    center: {
        textAlign: 'center',
    },
    
    right: {
        textAlign: 'right',
    },
    
    
    brandColor: {
        color: colors.bmwLightBlue['main']
    },
    
    muted: {
        color: colors.muted[400]
    },

    danger: {
        color: colors.red[500],
    },

    warning: {
        color: colors.orange[500],
    },

    success: {
        color: colors.lime[500],
    },

    info: {
        color: colors.bmwLightBlue['main'],
    },
    
    mutedDark: {
        color: colors.muted[600]
    },
    
    white: {
        color: 'white'
    },
    
    truncate: {
        overflow: 'hidden', 
        textOverflow: 'ellipsis'
    },

    nowrap: {
        whiteSpace: 'nowrap'
    },
    shadowSmall: {
        textShadowColor: 'rgba(0, 0, 0, 0.75)',
        textShadowOffset: {width: -1, height: 1},
        textShadowRadius: 10
    },

    ...Object.keys(colors)?.map(color => {
        
        if (['black', 'white'].includes(color)) 
            return null;

        let colorCodes = {};

        Object.keys(colors[color])?.forEach(weight => {
            colorCodes[`${color}.${weight}`] = {
                color: colors[color][weight]
            }
        });

        return colorCodes
    })?.filter(Boolean)?.reduce((acc, curr) => ({...acc, ...curr}), {})
});

export const text = {
    ..._text,
    heading: _text.h1,
    subtitle: {..._text.small, fontWeight: 'normal', color: colors.muted[500]},
    text: _text.p
}

export const display = StyleSheet.create({
    block: {
        display: 'block'
    },
    inline: {
        display: 'inline'
    },
    inlineBlock: {
        display: 'inline-block'
    },
    flex: {
        display: 'flex'
    },
    none: {
        display: 'none'
    },
    grid: {
        display: 'grid'
    },
});

export const position = StyleSheet.create({
    absolute: {
        position: 'absolute'
    },
    relative: {
        position: 'relative'
    },
    fixed: {
        position: 'fixed'
    },
    sticky: {
        position: 'sticky'
    },
    static: {
        position: 'static'
    }
});

export const ratio = StyleSheet.create({
    square: {
        aspectRatio: 1
    },
    widescreen: {
        aspectRatio: 16/9
    },
    screen: {
        aspectRatio: 4/3
    },
});

export const elements = StyleSheet.create({
    absoluteFill: {
        ...StyleSheet.absoluteFillObject
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    center: {
        justifyContent: 'center',
        alignItems: 'center'
    },
    dashedLine : {
        width: 1,
        borderColor : colors.coolGray[200],
        borderWidth: 2.130,
        borderStyle: 'dashed',
        marginLeft: 8
    },
    shadow: {
        ...Platform.select({
            native: {
                shadowColor: '#000000',
                shadowOffset: {width: 0, height: 0},
                shadowOpacity: 0.2,
                shadowRadius: 3,
                elevation: 3,
            },
            web: {
                boxShadow: '0px 0px 12px -3px rgba(0,0,0,0.4)'
            }
        })
    }
});

export const rounded = StyleSheet.create({
    leftTop: {
        borderTopLeftRadius: VIEW_BORDER_RADIUS
    },
    rightTop: {
        borderTopRightRadius: VIEW_BORDER_RADIUS
    },
    leftBottom: {
        borderBottomLeftRadius: VIEW_BORDER_RADIUS
    },
    rightBottom: {
        borderBottomRightRadius: VIEW_BORDER_RADIUS
    },
    top: {
        borderTopLeftRadius: VIEW_BORDER_RADIUS,
        borderTopRightRadius: VIEW_BORDER_RADIUS
    },
    bottom: {
        borderBottomLeftRadius: VIEW_BORDER_RADIUS,
        borderBottomRightRadius: VIEW_BORDER_RADIUS
    },
    full: {
        borderRadius: VIEW_BORDER_RADIUS
    },
    left: {
        borderTopLeftRadius: VIEW_BORDER_RADIUS,
        borderBottomLeftRadius: VIEW_BORDER_RADIUS
    },
    right: {
        borderTopRightRadius: VIEW_BORDER_RADIUS,
        borderBottomRightRadius: VIEW_BORDER_RADIUS
    }
});